import { Text, View } from '@react-pdf/renderer'
import {
  AutoComplete,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
} from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import compact from 'lodash/compact'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import {
  ComponentStats,
  InspectionSummaryStats,
  SectionStats,
} from 'pages/reporting/reports/rug/components/InspectionSummaryStats'
import {
  DEFAULT_VALUES,
  REPORT_COLORS,
  SPACING,
  TYPOGRAPHY,
} from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { useComponentNominalRangeString } from 'pages/reporting/reports/rug/hooks/useComponentNominalRangeString'
import {
  Resolution,
  RugReportSectionId,
  SummarySectionData,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  ComponentId,
  ReportCoverageScopeOptions,
  RugSectionStats,
} from 'pages/reporting/reports/rug/types'
import { hasSectionData } from 'pages/reporting/reports/rug/utils/calculateRUGStatsForSection'
import { formatInspectionTemperature } from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import { SourceIndicator } from 'pages/reporting/shared/components/SourceIndicator'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useMemo } from 'react'
import {
  GeckoAssetModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'
import {
  autocompleteFilterOptionWithoutExactMatch,
  autocompletePreviewDisabledProps,
} from 'utils/fde/antD'

const RESOLUTION_UNIT_OPTIONS = [
  {
    value: GeckoMeasurementUnit.Tubes.id,
    label: GeckoMeasurementUnit.Tubes.name,
  },
  {
    value: GeckoMeasurementUnit.Inches.id,
    label: GeckoMeasurementUnit.Inches.name,
  },
  {
    value: GeckoMeasurementUnit.Millimeters.id,
    label: GeckoMeasurementUnit.Millimeters.name,
  },
  {
    value: GeckoMeasurementUnit.Feet.id,
    label: GeckoMeasurementUnit.Feet.name,
  },
  {
    value: GeckoMeasurementUnit.Meters.id,
    label: GeckoMeasurementUnit.Meters.name,
  },
]

const requiredSummaryPageFields = [
  'summarySectionData.inspectionResolution.width',
  'summarySectionData.inspectionResolution.height',
  'summarySectionData.inspectionResolution.widthUnit',
  'summarySectionData.inspectionResolution.heightUnit',
  'summarySectionData.deliverableResolution.width',
  'summarySectionData.deliverableResolution.height',
  'summarySectionData.deliverableResolution.widthUnit',
  'summarySectionData.deliverableResolution.heightUnit',
  'summarySectionData.coverageScope',
  'summarySectionData.corrosionBLUF',
]

const CORROSION_BLUF_TEMPLATES = {
  'Mode 2': `The inspection results presented in this report stem from the usage of a Mode 2 (interface-to-echo) measurement approach. The selection of this methodology was influenced by the lack of multiple backwall reflections, which are commonly caused by corrosion, high signal attenuation, and/or unsuitable inspection surface conditions.

The Mode 2 technique takes into account the base material as well as any potential coatings. An average thickness of the coating, determined from the inspection findings, has been calculated and a correction has been applied to the minimum thickness values presented in this report.`,
  'Surface Condition (standard)': `Inadequate inspection surface conditions, typically due to factors such as top-side corrosion, significant surface roughness, and poor coating conditions led to occasional gaps in the collected ultrasonic data.`,
  'Surface Condition (unresolvable damage)': `Inadequate inspection surface conditions, typically due to factors such as top-side corrosion, significant surface roughness, and poor coating conditions led to occasional gaps in the collected ultrasonic data and a reduced near-surface resolution.`,
  'Inclusions/Laminations': `Intermittent areas of non-metallic defects (laminations and/or inclusions) from the fabrication process were identified. These defects show no signs of in-service degradation and were excluded from the final thickness results.`,
  'Aliasing/Binning (Tanks)': `Variations in the heatmap may be observed at the intersection of adjacent shell courses with varying nominal thicknesses. These areas are indicative of data binning overlap from the adjacent shell course. The minimum thicknesses represented in this report for each shell course is exclusive of overlap readings.`,
  'Access restrictions': `Various geometrical obstructions present, restricting access for data collection (ex. Nozzles, manways, stairs, supports, conduit, etc.).`,
  'Weld Line Data Omission': `Data points collected 6" above and below horizontal weld lines are omitted from low readings table due to potential localization errors.`,
} as const

const formControlStyle = {
  width: '100%',
}

interface AxisReferenceProps {
  componentName: string
  xAxisStartLocation?: string
  yAxisStartLocation?: string
}

const stripAxisPrefix = (value: string) =>
  value.replace(/^[XY]-axis\s*=\s*/i, '')

const AxisReference: React.FC<AxisReferenceProps> = ({
  componentName,
  xAxisStartLocation = DEFAULT_VALUES.X_AXIS_START_LOCATION,
  yAxisStartLocation = DEFAULT_VALUES.Y_AXIS_START_LOCATION,
}) => {
  const axisStartSummary = `${DEFAULT_VALUES.AXIS_REFERENCE_PREFIX} X-axis = ${stripAxisPrefix(xAxisStartLocation)} | Y-axis = ${stripAxisPrefix(yAxisStartLocation)}`

  return (
    <View style={{ marginBottom: SPACING.MARGIN.SMALL }}>
      <Text
        style={{
          color: REPORT_COLORS.VALUE_TEXT,
          fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
        }}>
        {componentName} — {axisStartSummary}
      </Text>
    </View>
  )
}

export const buildInspectionSummaryText = ({
  readingCount,
  assetType,
  assetName,
  maxWallLoss,
}: {
  readingCount?: number
  assetType: string
  assetName: string
  maxWallLoss: string
}): string => {
  const readings =
    readingCount !== undefined
      ? `A total of ${readingCount.toLocaleString()} UT readings were collected on the ${assetType} (${assetName}) with RUG.`
      : `UT readings were collected on the ${assetType} (${assetName}) with RUG.`

  return `${readings} The RUG Inspection revealed indications of corrosion, either general wall loss and/or pitting with a maximum of ${maxWallLoss} wall loss from the nominal thickness.`
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    statsBySection,
    statsByComponent,
    inspectionMetadata,
    componentToNominalRange,
    selectedComponents,
    sectionsByComponent,
    formatMeasurement,
  } = useRugReportOps()

  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const summaryData = useMemo(
    () => formData.summarySectionData,
    [formData.summarySectionData]
  )

  const nominalRangeStr = useComponentNominalRangeString(
    componentToNominalRange
  )
  const inspectionTemperature = useMemo(
    () => formatInspectionTemperature(mainInfo.inspectionTemperature),
    [mainInfo.inspectionTemperature]
  )

  const componentStats: ComponentStats[] = useMemo(() => {
    return (
      compact(
        selectedComponents?.map((component) => {
          const statsForWholeComponent = statsByComponent?.get(
            component.id as ComponentId
          )
          const statsForComponentSections = statsBySection?.get(
            component.id as ComponentId
          )

          if (statsForComponentSections) {
            const sectionable = sectionsByComponent?.get(
              component.id as ComponentId
            )
            // Tank/cone courses are stored top-down; boiler walls are already left-to-right.
            const orderedSections = sectionable?.splitByX
              ? sectionable.sections
              : sectionable?.sections.toReversed()

            const buildRow = (
              sectionName: string,
              sectionStats: RugSectionStats
            ): SectionStats => {
              const nominalRange = componentToNominalRange?.get(
                component.id as ComponentId
              )
              const fallbackNominalThickness =
                nominalRange &&
                GeckoMeasurement.Equivalent(nominalRange.min, nominalRange.max)
                  ? nominalRange.min
                  : undefined
              const nominalThickness =
                sectionStats.nominalThickness ?? fallbackNominalThickness
              let minimumThickness = GeckoMeasurement.CreateFromValue(
                Number.POSITIVE_INFINITY,
                GeckoMeasurementUnit.Inches
              )
              sectionStats.minThicknesses.forEach((currentMinThickness) => {
                if (currentMinThickness.thickness.lt(minimumThickness)) {
                  minimumThickness = currentMinThickness.thickness
                }
              })

              return {
                sectionName,
                minThickness: minimumThickness,
                averageThickness: sectionStats.averageThickness,
                wallLossPercent: sectionStats.maxWallLossPercent,
                nominalThickness,
                tMin: sectionStats.tMin,
              }
            }

            const sections = orderedSections?.length
              ? compact(
                  orderedSections.map((section) => {
                    const sectionStats = statsForComponentSections.get(
                      section.name
                    )
                    if (!sectionStats || !hasSectionData(sectionStats)) {
                      return undefined
                    }

                    return {
                      ...buildRow(section.name, sectionStats),
                    }
                  })
                )
              : compact(
                  Array.from(statsForComponentSections.entries()).map(
                    ([sectionName, sectionStats]) => {
                      if (!hasSectionData(sectionStats)) {
                        return undefined
                      }

                      return buildRow(sectionName, sectionStats)
                    }
                  )
                )

            return sections.length
              ? {
                  componentId: component.id as ComponentId,
                  componentName: component.data.name,
                  isWholeComponent: false,
                  sections,
                }
              : undefined
          } else if (statsForWholeComponent) {
            const nominalRange = componentToNominalRange?.get(
              component.id as ComponentId
            )
            const fallbackNominalThickness =
              nominalRange &&
              GeckoMeasurement.Equivalent(nominalRange.min, nominalRange.max)
                ? nominalRange.min
                : undefined
            const nominalThickness =
              statsForWholeComponent.nominalThickness ??
              fallbackNominalThickness
            let minimumThickness = GeckoMeasurement.CreateFromValue(
              Number.POSITIVE_INFINITY,
              GeckoMeasurementUnit.Inches
            )
            statsForWholeComponent.minThicknesses.forEach(
              (currentMinThickness) => {
                if (currentMinThickness.thickness.lt(minimumThickness)) {
                  minimumThickness = currentMinThickness.thickness
                }
              }
            )
            return {
              componentId: component.id as ComponentId,
              componentName: component.data.name,
              isWholeComponent: true,
              sections: [
                {
                  sectionName: component.data.name,
                  minThickness: minimumThickness,
                  averageThickness: statsForWholeComponent.averageThickness,
                  wallLossPercent: statsForWholeComponent.maxWallLossPercent,
                  nominalThickness,
                  tMin: statsForWholeComponent.tMin,
                },
              ],
            }
          }

          return undefined
        })
      ) ?? []
    )
  }, [
    statsBySection,
    statsByComponent,
    selectedComponents,
    sectionsByComponent,
    componentToNominalRange,
  ])

  const sectionStats = useMemo(
    () => componentStats.flatMap(({ sections }) => sections),
    [componentStats]
  )

  const maxWallLossPercent = useMemo(() => {
    const wallLossPercentages = sectionStats.flatMap(({ wallLossPercent }) =>
      wallLossPercent ? [wallLossPercent] : []
    )

    return wallLossPercentages.reduce<GeckoMeasurement | undefined>(
      (max, wallLossPercent) =>
        !max || wallLossPercent.gt(max) ? wallLossPercent : max,
      undefined
    )
  }, [sectionStats])

  const formatResolutionDimension = (value: number, unit: string) => {
    const isTubeUnit = unit === 'tube' || unit === GeckoMeasurementUnit.Tubes.id
    if (isTubeUnit) {
      return `${value} ${value === 1 ? 'tube' : 'tubes'}`
    }

    return formatMeasurement({
      measurement: GeckoMeasurement.CreateFromValue(
        value,
        GeckoMeasurementUnit.GetOrCreate(unit)
      ),
      variant: 'narrow',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    })
  }

  const formatResolution = (
    resolution: Resolution | undefined
  ): string | null => {
    if (!resolution || !resolution.width || !resolution.height) {
      return null
    }

    const widthStr = formatResolutionDimension(
      resolution.width,
      resolution.widthUnit
    )
    const heightStr = formatResolutionDimension(
      resolution.height,
      resolution.heightUnit
    )

    if (resolution.angles) {
      return `${widthStr} x ${resolution.angles} angles x ${heightStr}`
    }

    return `${widthStr} x ${heightStr}`
  }

  const totalUTReadingsStr = buildInspectionSummaryText({
    readingCount: inspectionMetadata.reported_ascan_count,
    assetType: mainInfo.assetType,
    assetName: mainInfo.assetName,
    maxWallLoss: maxWallLossPercent
      ? (formatMeasurement({
          measurement: maxWallLossPercent,
          minimumFractionDigits: 2,
          variant: 'narrow',
          maximumFractionDigits: 2,
        }) ?? 'N/A')
      : 'N/A',
  })

  return (
    <>
      <ReportPage
        id='inspectionSummary'
        tracker={{
          sectionId: RugReportSectionId.SUMMARY,
          onRender,
          registerOnce: false,
        }}
        header={<PageHeader title='Inspection Summary' />}
        footer={
          <PageFooter
            jobNumber={mainInfo.jobNumber}
            revisionNumber={formData.revisionMetadata.revisionNumber}
          />
        }
        contentStyle={PDFStyles.content}>
        <View style={{ marginBottom: 16 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: 'bold',
              marginBottom: 8,
              color: REPORT_COLORS.HEADER_TEXT,
            }}>
            Inspection Scope:
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            Gecko Robotics was contracted to perform a Rapid Ultrasonic Gridding
            (RUG) Examination on the {mainInfo.assetType} ({mainInfo.assetName})
            at the {mainInfo.customerName} facility in{' '}
            {mainInfo.customerLocation}.
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            {mainInfo.customerName} identified {summaryData.coverageScope} for
            the RUG inspection. The inspection resolution is{' '}
            {formatResolution(summaryData.inspectionResolution)} and the
            deliverable resolution is{' '}
            {formatResolution(summaryData.deliverableResolution)}.
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            The material for the {mainInfo.assetType} is{' '}
            {mainInfo.assetMaterial} with a nominal thickness of{' '}
            {nominalRangeStr}.
            {inspectionTemperature
              ? ` The surface temperature was ${inspectionTemperature} at the time of inspection.`
              : ''}
          </Text>
        </View>

        <View style={{ marginBottom: 16 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: 'bold',
              marginBottom: 8,
              color: REPORT_COLORS.HEADER_TEXT,
            }}>
            Inspection Summary:
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            {totalUTReadingsStr}
          </Text>
        </View>

        <InspectionSummaryStats
          componentStats={componentStats}
          showComponentNames={(selectedComponents?.length ?? 0) > 1}
          useTable={summaryData.useTableForSummary ?? false}
          includeTMin={summaryData.includeTMin ?? true}
          formatMeasurement={formatMeasurement}
        />

        <View style={{ marginBottom: 16 }}>
          {selectedComponents?.map((component) => {
            const axisLocations =
              formData.mainInfo.componentAxisStartLocations?.[component.id]

            return (
              <AxisReference
                key={component.id}
                componentName={component.data.name}
                xAxisStartLocation={axisLocations?.xAxisStartLocation}
                yAxisStartLocation={axisLocations?.yAxisStartLocation}
              />
            )
          })}
        </View>

        {summaryData.corrosionBLUF && (
          <View style={{ marginBottom: 16 }}>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 'bold',
                marginBottom: 8,
                color: REPORT_COLORS.HEADER_TEXT,
              }}>
              Corrosion Summary:
            </Text>
            <Text style={{ fontSize: 10 }}>{summaryData.corrosionBLUF}</Text>
          </View>
        )}

        {summaryData.inspectionSummaryNotes && (
          <View>
            <Text
              style={{
                fontSize: 10,
                fontWeight: 'bold',
                marginBottom: 4,
                color: REPORT_COLORS.HEADER_TEXT,
              }}>
              Notes
            </Text>
            <Text style={{ fontSize: 10 }}>
              {summaryData.inspectionSummaryNotes}
            </Text>
          </View>
        )}
      </ReportPage>
      <AddendumPages
        sectionId={RugReportSectionId.SUMMARY}
        sectionTitle='Inspection Summary'
      />
    </>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<SummarySectionData>()
  const {
    formData,
    handleSummarySectionFinish,
    selectedAssetModel,
    fieldSources,
  } = useRugReportOps()

  const sourceFor = React.useCallback(
    (path: string) => fieldSources?.[path],
    [fieldSources]
  )

  const initialValues = React.useMemo(
    () => ({
      ...formData.summarySectionData,
    }),
    [formData.summarySectionData]
  )

  return (
    <StyledForm
      form={form}
      name='rugSummaryForm'
      onFinish={handleSummarySectionFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        label={
          <Space>
            <span>Coverage Scope</span>
            <SourceIndicator
              form={form}
              name='coverageScope'
              source={sourceFor('summarySectionData.coverageScope')}
              initialValue={form.getFieldValue('coverageScope')}
            />
          </Space>
        }
        name='coverageScope'
        rules={[{ required: true, message: 'Please enter coverage scope' }]}>
        <AutoComplete
          {...autocompletePreviewDisabledProps}
          style={formControlStyle}
          options={(selectedAssetModel
            ? (ReportCoverageScopeOptions[selectedAssetModel?.data.type] ?? [])
            : []
          ).map((status) => {
            return { value: status }
          })}
          placeholder='Select a saved scope or type a custom scope'
          filterOption={autocompleteFilterOptionWithoutExactMatch}
        />
      </Form.Item>

      <Form.Item label='Display Summary Format' name='useTableForSummary'>
        <Radio.Group
          optionType='button'
          buttonStyle='solid'
          options={[
            { label: 'Narrative', value: false },
            { label: 'Table', value: true },
          ]}
        />
      </Form.Item>

      <Form.Item label='Include T-Min Column' name='includeTMin'>
        <Radio.Group
          optionType='button'
          buttonStyle='solid'
          options={[
            { label: 'No', value: false },
            { label: 'Yes', value: true },
          ]}
        />
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Corrosion BLUF</span>
            <Dropdown
              menu={{
                items: Object.keys(CORROSION_BLUF_TEMPLATES).map((key) => ({
                  key,
                  label: key,
                  onClick: () => {
                    const currentValue =
                      form.getFieldValue('corrosionBLUF') || ''
                    const templateText =
                      CORROSION_BLUF_TEMPLATES[
                        key as keyof typeof CORROSION_BLUF_TEMPLATES
                      ]
                    const newValue = currentValue
                      ? `${currentValue}\n\n${templateText}`
                      : templateText
                    form.setFieldValue('corrosionBLUF', newValue)
                  },
                })),
              }}
              trigger={['click']}>
              <span>
                <CantileverButton>Add Template</CantileverButton>
              </span>
            </Dropdown>
          </Space>
        }
        name='corrosionBLUF'
        rules={[{ required: true, message: 'Please enter corrosion BLUF' }]}>
        <Input.TextArea
          rows={6}
          placeholder='Bottom line up front summary of corrosion findings'
        />
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Inspection Resolution</span>
            <SourceIndicator
              form={form}
              name='inspectionResolution'
              source={sourceFor('summarySectionData.inspectionResolution')}
              initialValue={form.getFieldValue('inspectionResolution')}
            />
          </Space>
        }>
        <Space.Compact style={{ width: '100%' }}>
          <Form.Item
            name={['inspectionResolution', 'width']}
            noStyle
            rules={[{ required: true, message: 'Width required' }]}>
            <InputNumber
              style={{ width: 80 }}
              placeholder='Width'
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name={['inspectionResolution', 'widthUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>

          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px',
            }}>
            x
          </span>

          {selectedAssetModel?.data.type ===
            GeckoAssetModelType.LegacyBoiler && (
            <>
              <Form.Item name={['inspectionResolution', 'angles']} noStyle>
                <InputNumber style={{ width: 80 }} placeholder='Angles' />
              </Form.Item>

              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                }}>
                angles x
              </span>
            </>
          )}

          <Form.Item
            name={['inspectionResolution', 'height']}
            noStyle
            rules={[{ required: true, message: 'Height required' }]}>
            <InputNumber
              style={{ width: 80 }}
              placeholder='Height'
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name={['inspectionResolution', 'heightUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Deliverable Resolution</span>
            <SourceIndicator
              form={form}
              name='deliverableResolution'
              source={sourceFor('summarySectionData.deliverableResolution')}
              initialValue={form.getFieldValue('deliverableResolution')}
            />
          </Space>
        }>
        <Space.Compact style={{ width: '100%' }}>
          <Form.Item
            name={['deliverableResolution', 'width']}
            noStyle
            rules={[{ required: true, message: 'Width required' }]}>
            <InputNumber
              style={{ width: 80 }}
              placeholder='Width'
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name={['deliverableResolution', 'widthUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>

          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px',
            }}>
            x
          </span>

          {selectedAssetModel?.data.type ===
            GeckoAssetModelType.LegacyBoiler && (
            <>
              <Form.Item name={['deliverableResolution', 'angles']} noStyle>
                <InputNumber style={{ width: 80 }} placeholder='Angles' />
              </Form.Item>

              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                }}>
                angles x
              </span>
            </>
          )}

          <Form.Item
            name={['deliverableResolution', 'height']}
            noStyle
            rules={[{ required: true, message: 'Height required' }]}>
            <InputNumber
              style={{ width: 80 }}
              placeholder='Height'
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name={['deliverableResolution', 'heightUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      <Form.Item
        label='Inspection Summary Notes (Optional)'
        name='inspectionSummaryNotes'>
        <Input.TextArea
          rows={3}
          placeholder='Additional notes about the inspection...'
        />
      </Form.Item>

      <Form.Item>
        <CantileverButton
          primary
          onClick={() => form.submit()}
          style={{ width: '100%' }}>
          Save Inspection Summary
        </CantileverButton>
      </Form.Item>
    </StyledForm>
  )
}

export const InspectionSummary: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: RugReportSectionId.SUMMARY,
  description:
    'Inspection scope, summary statistics, and section-by-section breakdown',
  title: 'Inspection Summary',
  requiredFields: requiredSummaryPageFields,
}
